import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import {
  ColorMappingItem,
  DateType,
  UtilisationClass,
  UtilisationLevel,
} from 'src/app/model';
import * as CryptoJS from 'crypto-js';
import { environment } from 'src/environments/environment';
import { Messages } from 'src/app/constants/messages';

@Injectable()
export class GeneralService {
  constructor(private _datePipe: DatePipe) {}

  /**
   * Converts object to array
   * @param object
   * @returns object to array
   */
  convertObjectToArray(object: object): { key: string; value: any }[] {
    return Object.entries(object).map(([key, value]) => ({ key, value }));
  }

  /**
   * Generates array of years
   * @param [futureYear]
   * @param [pastYear]
   * @returns array of years
   */
  generateArrayOfYears(
    futureYear: number = 0,
    pastYear: number = 10
  ): number[] {
    const maximumYear = new Date().getFullYear() + futureYear;
    const minimumYear = new Date().getFullYear() - pastYear;
    let years = [];
    for (let index = maximumYear; index >= minimumYear; index--) {
      years.push(index);
    }
    return years;
  }

  /**
   * Determines whether date is
   * @param value
   * @returns true if date
   */
  isDate(value: string): boolean {
    const regex = /(\d{4})-(\d{1,2})-(\d{1,2})/g;
    return regex?.test(value);
  }

  /**
   * Determines whether empty object is
   * @param object
   * @returns true if empty object
   */
  isEmptyObject(object: object): boolean {
    return Object.keys(object)?.length ? false : true;
  }

  /**
   * Determines whether object is
   * @param value
   * @returns
   */
  isObject(value: any) {
    return value && typeof value === 'object' && value?.constructor === Object;
  }

  /**
   * Compares array
   * @template T
   * @param a
   * @param b
   * @returns true if array
   */
  compareArray<T>(a: T[], b: T[]): boolean {
    if (a?.length !== b?.length) return false;
    const elements = new Set([...a, ...b]);
    for (const element of elements) {
      const count1 = a?.filter((value) => value === element)?.length;
      const count2 = b?.filter((value) => value === element)?.length;
      if (count1 !== count2) return false;
    }
    return true;
  }

  /**
   * Gets today date
   * @param [format]
   * @returns
   */
  getTodayDate(format = 'yyyy-MM-dd') {
    return this._datePipe.transform(new Date(), format);
  }

  /**
   * Gets previous date by year
   * @param [counter]
   * @param [format]
   * @returns
   */
  getPreviousDateByYear(counter: number = 1, format = 'yyyy-MM-dd') {
    const date = new Date();
    return this._datePipe.transform(
      date?.setFullYear(date.getFullYear() - counter),
      format
    );
  }

  /**
   * Gets timestamp in millisecond
   * @param date
   * @returns timestamp in millisecond
   */
  getTimestampInMillisecond(date: Date): number {
    return date ? new Date(date)?.getTime() : 0;
  }

  /**
   * Gets timestamp in second
   * @param date
   * @returns timestamp in second
   */
  getTimestampInSecond(date: Date): number {
    return date ? Math.floor(new Date(date)?.getTime() / 1000) : 0;
  }

  /**
   * Encrypts general service
   * @param value
   * @returns encrypt
   */
  encrypt(value: string): string {
    const SECRET_KEY = environment.secretKey;
    let k = CryptoJS.enc.Utf8.parse(SECRET_KEY.trim());
    let cypher = CryptoJS.AES.encrypt(value, k, { mode: CryptoJS.mode.ECB });
    return CryptoJS.enc.Hex.stringify(cypher.ciphertext);
  }

  /**
   * Gets utilisation class
   * @param value
   * @returns
   */
  getUtilisationClass(value: UtilisationLevel) {
    return Object.entries(UtilisationClass).find(
      ([key, className]) => key === value?.toUpperCase()
    )?.[1] as UtilisationClass;
  }

  /**
   * Creates array from length
   * @param length
   * @returns
   */
  createArrayFromLength(length: number) {
    return Array.from({ length: length }, (_, index) => index + 1);
  }

  /**
   * Removes property from object
   * @param object
   * @param property
   * @returns property from object
   */
  removePropertyFromObject(object: any, property: string): object {
    if (!this.isObject(object) || this.isEmptyObject(object)) {
      return null;
    }
    const { [property]: removedValue, ...newObject } = object;
    return newObject;
  }

  /**
   * Gets previous date by type
   * @param type
   * @param [counter]
   * @returns date of previous date by type
   */
  getPreviousDateByType(type: DateType, counter: number = 1): Date {
    const date = new Date();
    switch (type) {
      case 'millisec':
        date?.setMilliseconds(date.getMilliseconds() - counter);
        break;
      case 'sec':
        date?.setSeconds(date.getSeconds() - counter);
        break;
      case 'min':
        date?.setMinutes(date.getMinutes() - counter);
        break;
      case 'hour':
        date?.setHours(date.getHours() - counter);
        break;
      case 'day':
        date?.setDate(date.getDate() - counter);
        break;
      case 'month':
        date?.setMonth(date.getMonth() - counter);
        break;
      case 'year':
        date?.setFullYear(date.getFullYear() - counter);
        break;
    }
    return date;
  }

  /**
   * Formats date
   * @param date
   * @param [format]
   * @param [timezone]
   * @param [locale]
   * @returns date
   */
  formatDate(
    date: Date | string | number,
    format = 'yyyy-MM-dd',
    timezone?: string,
    locale?: string
  ): string | null {
    return this._datePipe.transform(date, format, timezone, locale);
  }

  /**
   * Gets index list from array
   * @template T
   * @param list
   * @param key
   * @returns index list from array
   */
  getIndexListFromArray<T>(list: T[], key: string): string[] {
    return list?.map((content) => content?.[key]);
  }

  /**
   * Sorts object by key
   * @param object
   * @param key
   * @param valueType
   * @returns object by key
   */
  sortObjectByKey(
    object: object[],
    key: string,
    valueType: 'string' | 'number'
  ): object[] {
    if (valueType === 'string') {
      return object.sort((firstElement, secondElement) => {
        const firstValue = firstElement?.[key].toLowerCase();
        const secondValue = secondElement?.[key].toLowerCase();
        if (firstValue < secondValue) {
          return -1;
        }
        if (firstValue > secondValue) {
          return 1;
        }
        return 0;
      });
    } else {
      return object?.sort(
        (firstElement, secondElement) =>
          firstElement?.[key] - secondElement?.[key]
      );
    }
  }

  /**
   * Gets boolean
   * @param value
   * @returns true if boolean
   */
  getBoolean(value): boolean {
    switch (value?.toLowerCase()) {
      case true:
      case 'true':
      case 1:
      case '1':
      case 'on':
      case 'yes':
        return true;
      default:
        return false;
    }
  }

  /**
   * Determines whether array in
   * @param needle
   * @param haystack
   * @param [returnType]
   * @returns array
   */
  inArray(
    needle: any,
    haystack: any[],
    returnType: 'boolean' | 'number' = 'boolean'
  ): number | boolean {
    return returnType === 'boolean'
      ? haystack?.includes(needle)
      : haystack?.indexOf(needle) ?? -1;
  }

  /**
   * Replaces general service
   * @param value
   * @param subString
   * @param replaceValue
   * @returns replace
   */
  replace(value: string, subString: any, replaceValue: any): string {
    return value?.replace(subString, replaceValue);
  }

  /**
   * Gets random number
   * @param min
   * @param max
   * @returns random number
   */
  getRandomNumber(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  /**
   * Gets start end time by date
   * @returns start end time by date
   */
  getStartEndTimeByDate(): [number, number] {
    const startTime = new Date()?.setHours(0, 0, 0, 0);
    const endTime = new Date()?.setHours(23, 59, 59, 999);
    return [startTime, endTime];
  }

  /**
   * Gets error message
   * @param fieldName
   * @param errorKey
   * @returns error message
   */
  getErrorMessage(fieldLabel: string, errorKey: string): string {
    let errorMessage: string;
    // Set message
    switch (errorKey) {
      case 'required':
        errorMessage = Messages.FORM_VALIDATOR_REQUIRED_MESSAGE?.replace(
          '[FIELD_NAME]',
          fieldLabel
        );
        break;

      default:
        errorMessage = Messages.FORM_VALIDATOR_VALID_MESSAGE?.replace(
          '[VALIDATOR]',
          fieldLabel
        );
        break;
    }
    return errorMessage;
  }

  /**
   * Gets descendant prop
   * @param object
   * @param descendant
   * @param splitter
   * @returns descendant prop
   */
  getDescendantProp(
    object: Object,
    descendant: string,
    splitter: string
  ): string {
    const list = descendant?.split(splitter);
    while (list?.length && (object = object?.[list?.shift()]));
    return object as string;
  }
}
